/**
 * Use BUS_ASSET_BASE_IMAGES's type directly if you want TS to strictly infer the valid keys
 */
export type BusAssetBaseImagesLoose = Readonly<
    Record<
        string,
        Readonly<{
            name: string;
            position: number;
            position_admin: number;
            label: string;
            group: string;
            description: string;
            performTextInpaint: boolean;
            requiredForToBeScrapped: boolean;
        }>
    >
>;

export const BUS_ASSET_BASE_IMAGES = {
    photo_3_4_front_side_right_doors_closed: {
        name: 'photo_3_4_front_side_right_doors_closed',
        position: 10,
        position_admin: 220,
        label: '3/4 front right doors',
        group: 'exteriors',
        description: 'A 3/4 angled photo of the front side of the vehicle from the right, with doors closed.',
        performTextInpaint: true,
        requiredForToBeScrapped: true,
    },
    photo_front: {
        name: 'photo_front',
        position: 20,
        position_admin: 30,
        label: 'front photo',
        group: 'exteriors',
        description: 'A photo capturing the front view of the vehicle.',
        performTextInpaint: true,
        requiredForToBeScrapped: true,
    },
    photo_dashboard_on_for_number_km_and_lights_on: {
        name: 'photo_dashboard_on_for_number_km_and_lights_on',
        position: 30,
        position_admin: 360,
        label: 'dashboard lights on',
        group: 'interiors',
        description: "A photo showing the dashboard with the vehicle's odometer reading and warning lights illuminated.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_3_4_front_side_left: {
        name: 'photo_3_4_front_side_left',
        position: 40,
        position_admin: 40,
        label: '3/4 front left',
        group: 'exteriors',
        description: 'A 3/4 angled photo of the front side of the vehicle from the left.',
        performTextInpaint: true,
        requiredForToBeScrapped: true,
    },
    photo_rear_hatch_closed: {
        name: 'photo_rear_hatch_closed',
        position: 50,
        position_admin: 130,
        label: 'rear hatch closed',
        group: 'exteriors',
        description: 'A photo showing the rear of the vehicle with the hatch closed.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_plate_seats: {
        name: 'photo_plate_seats',
        position: 1180,
        position_admin: 320,
        label: 'seat plate photo',
        group: 'interiors',
        description: 'A photo showing the seat plate inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_plate_vehicle: {
        name: 'photo_plate_vehicle',
        position: 1190,
        position_admin: 310,
        label: 'vehicle plate photo',
        group: 'exteriors',
        description: "A photo showing the vehicle's plate.",
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    photo_tire_front_left_measurements: {
        name: 'photo_tire_front_left_measurements',
        position: 1200,
        position_admin: 50,
        label: 'front left tire measurements',
        group: 'tire-sizes',
        description: 'A photo showing the measurements of the front left tire.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_side_left: {
        name: 'photo_side_left',
        position: 90,
        position_admin: 70,
        label: 'left side photo',
        group: 'exteriors',
        description: 'A photo showing the left side of the vehicle.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_luggage: {
        name: 'photo_luggage',
        position: 100,
        position_admin: 80,
        label: 'luggage rack photo',
        group: 'exteriors',
        description: 'A photo showing the luggage rack in the vehicle.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_internal_frame_for_potential_rust: {
        name: 'photo_internal_frame_for_potential_rust',
        position: 110,
        position_admin: 90,
        label: 'internal frame rust check',
        group: 'exteriors',
        description: 'A photo showing the internal frame of the vehicle to check for potential rust.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_under_carpet_if_carpet_present: {
        name: 'photo_under_carpet_if_carpet_present',
        position: 1310,
        position_admin: 95,
        label: 'under carpet photo',
        group: 'exteriors',
        description: 'A photo showing the area under the carpet if present.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_tire_rear_left: {
        name: 'photo_tire_rear_left',
        position: 1210,
        position_admin: 110,
        label: 'rear left tire photo',
        group: 'tire-sizes',
        description: 'A photo showing the rear left tire of the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_3_4_side_left_rear: {
        name: 'photo_3_4_side_left_rear',
        position: 120,
        position_admin: 120,
        label: '3/4 left rear side',
        group: 'exteriors',
        description: 'A 3/4 angled photo of the left rear side of the vehicle.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_chassis_vin: {
        name: 'photo_chassis_vin',
        position: 1220,
        position_admin: 10,
        label: 'chassis VIN photo',
        group: 'technical-data',
        description: "A photo of the vehicle's chassis VIN.",
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    photo_rear_hatch_open: {
        name: 'photo_rear_hatch_open',
        position: 250,
        position_admin: 140,
        label: 'rear hatch open',
        group: 'exteriors',
        description: 'A photo showing the rear of the vehicle with the hatch open.',
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    engine_plate_photo_if_present: {
        name: 'engine_plate_photo_if_present',
        position: 1230,
        position_admin: 165,
        label: 'engine plate photo',
        group: 'exteriors',
        description: 'A photo showing the engine plate if present.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_compressor_air_conditioning: {
        name: 'photo_compressor_air_conditioning',
        position: 260,
        position_admin: 150,
        label: 'air conditioning compressor',
        group: 'exteriors',
        description: 'A photo showing the air conditioning compressor.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_fire_extinguishing_system: {
        name: 'photo_fire_extinguishing_system',
        position: 270,
        position_admin: 160,
        label: 'fire extinguishing system',
        group: 'exteriors',
        description: 'A photo showing the fire extinguishing system of the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_preheater_webasto_if_present: {
        name: 'photo_preheater_webasto_if_present',
        position: 280,
        position_admin: 170,
        label: 'preheater webasto photo',
        group: 'exteriors',
        description: 'A photo of the preheater (webasto) system if present.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_3_4_side_right_rear_doors_open: {
        name: 'photo_3_4_side_right_rear_doors_open',
        position: 130,
        position_admin: 250,
        label: '3/4 right rear doors open',
        group: 'exteriors',
        description: 'A 3/4 angled photo showing the right rear side with doors open.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_3_4_side_right_rear_doors_closed: {
        name: 'photo_3_4_side_right_rear_doors_closed',
        position: 140,
        position_admin: 190,
        label: '3/4 right rear doors closed',
        group: 'exteriors',
        description: 'A 3/4 angled photo showing the right rear side with doors closed.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_side_right_doors_closed: {
        name: 'photo_side_right_doors_closed',
        position: 150,
        position_admin: 200,
        label: 'right side doors closed',
        group: 'exteriors',
        description: 'A photo showing the right side of the vehicle with doors closed.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_side_right_doors_open: {
        name: 'photo_side_right_doors_open',
        position: 160,
        position_admin: 240,
        label: 'right side doors open',
        group: 'exteriors',
        description: 'A photo showing the right side of the vehicle with doors open.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_3_4_side_right_front_doors_open: {
        name: 'photo_3_4_side_right_front_doors_open',
        position: 145,
        position_admin: 230,
        label: '3/4 right front doors open',
        group: 'exteriors',
        description: 'A 3/4 angled photo showing the right front side of the vehicle with doors open.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    front_photo_per_door_doors_open_door_1: {
        name: 'front_photo_per_door_doors_open_door_1',
        position: 180,
        position_admin: 300,
        label: 'front door 1 open',
        group: 'exteriors',
        description: 'A photo of the front door 1 in an open position.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    front_photo_per_door_doors_open_door_2: {
        name: 'front_photo_per_door_doors_open_door_2',
        position: 190,
        position_admin: 270,
        label: 'front door 2 open',
        group: 'exteriors',
        description: 'A photo of the front door 2 in an open position.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    front_photo_per_door_doors_open_door_3: {
        name: 'front_photo_per_door_doors_open_door_3',
        position: 200,
        position_admin: 260,
        label: 'front door 3 open',
        group: 'exteriors',
        description: 'A photo of the front door 3 in an open position.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_platform_open_platform: {
        name: 'photo_platform_open_platform',
        position: 210,
        position_admin: 215,
        label: 'platform open photo',
        group: 'exteriors',
        description: 'A photo of the platform in an open position.',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_platform_code: {
        name: 'photo_platform_code',
        position: 220,
        position_admin: 216,
        label: 'platform code photo',
        group: 'exteriors',
        description: "A photo showing the platform's code.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_drive_seat: {
        name: 'photo_drive_seat',
        position: 290,
        position_admin: 340,
        label: "driver's seat photo",
        group: 'interiors',
        description: "A photo of the driver's seat.",
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    photo_seat_hostess: {
        name: 'photo_seat_hostess',
        position: 300,
        position_admin: 440,
        label: 'hostess seat photo',
        group: 'interiors',
        description: 'A photo of the hostess seat in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_buttons_e_panels_on: {
        name: 'photo_buttons_e_panels_on',
        position: 320,
        position_admin: 370,
        label: 'buttons panels on',
        group: 'interiors',
        description: "A photo showing the vehicle's buttons and panels in an 'on' state.",
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    photo_steering_wheel: {
        name: 'photo_steering_wheel',
        position: 330,
        position_admin: 350,
        label: 'steering wheel photo',
        group: 'interiors',
        description: 'A photo of the steering wheel in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_any_devices_onboard_1: {
        name: 'photo_any_devices_onboard_1',
        position: 340,
        position_admin: 400,
        label: 'onboard devices 1',
        group: 'interiors',
        description: 'A photo of any onboard devices in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_any_devices_onboard_2: {
        name: 'photo_any_devices_onboard_2',
        position: 350,
        position_admin: 410,
        label: 'onboard devices 2',
        group: 'interiors',
        description: 'A photo of any onboard devices in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_any_devices_onboard_3: {
        name: 'photo_any_devices_onboard_3',
        position: 360,
        position_admin: 420,
        label: 'onboard devices 3',
        group: 'technical-data',
        description: 'A photo of any onboard devices in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_hatches_roof_1: {
        name: 'photo_hatches_roof_1',
        position: 370,
        position_admin: 560,
        label: 'roof hatch 1',
        group: 'interiors',
        description: 'A photo of the roof hatch number 1.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_hatches_roof_2: {
        name: 'photo_hatches_roof_2',
        position: 380,
        position_admin: 570,
        label: 'roof hatch 2',
        group: 'interiors',
        description: 'A photo of the roof hatch number 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_hatches_roof_3: {
        name: 'photo_hatches_roof_3',
        position: 390,
        position_admin: 575,
        label: 'roof hatch 3',
        group: 'interiors',
        description: 'A photo of the roof hatch number 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_interior_contromarcia_inquadrare_tutti_i_seats_height_seat_autista: {
        name: 'photo_interior_contromarcia_inquadrare_tutti_i_seats_height_seat_autista',
        position: 400,
        position_admin: 480,
        label: 'interior reverse seats driver height',
        group: 'interiors',
        description: 'A photo showing the interior seats in reverse view, including driver seat height.',
        requiredForToBeScrapped: true,
        performTextInpaint: false,
    },
    photo_interior_reverse_view_from_middle_aisle: {
        name: 'photo_interior_reverse_view_from_middle_aisle',
        position: 410,
        position_admin: 490,
        label: 'reverse view middle aisle',
        group: 'interiors',
        description: 'A reverse view photo from the middle aisle of the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_interior_senso_di_marcia_inquadrare_tutti_i_seats_height_ultima_fila: {
        name: 'photo_interior_senso_di_marcia_inquadrare_tutti_i_seats_height_ultima_fila',
        position: 420,
        position_admin: 500,
        label: 'forward view all seats last row height',
        group: 'interiors',
        description: 'A forward view photo showing all seats, focusing on the height of the last row.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_interior_forward_view_from_middle_aisle: {
        name: 'photo_interior_forward_view_from_middle_aisle',
        position: 430,
        position_admin: 530,
        label: 'forward view middle aisle',
        group: 'interiors',
        description: 'A forward view photo from the middle aisle of the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_disabled_area: {
        name: 'photo_disabled_area',
        position: 440,
        position_admin: 580,
        label: 'disabled area photo',
        group: 'interiors',
        description: 'A photo showing the designated disabled area in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_folding_seat: {
        name: 'photo_folding_seat',
        position: 450,
        position_admin: 590,
        label: 'folding seat photo',
        group: 'interiors',
        description: 'A photo showing the folding seat in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_overhead_compartments: {
        name: 'photo_overhead_compartments',
        position: 460,
        position_admin: 510,
        label: 'overhead compartments photo',
        group: 'interiors',
        description: 'A photo showing the overhead compartments inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_detail_of_single_seat: {
        name: 'photo_detail_of_single_seat',
        position: 470,
        position_admin: 620,
        label: 'single seat detail',
        group: 'interiors',
        description: 'A detailed photo of a single seat in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_detail_of_mesh: {
        name: 'photo_detail_of_mesh',
        position: 480,
        position_admin: 630,
        label: 'mesh detail photo',
        group: 'interiors',
        description: 'A detailed photo of the mesh inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_table_detail: {
        name: 'photo_table_detail',
        position: 490,
        position_admin: 640,
        label: 'table detail photo',
        group: 'interiors',
        description: 'A detailed photo of the table inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_charging_usb_detail: {
        name: 'photo_charging_usb_detail',
        position: 500,
        position_admin: 650,
        label: 'usb charging detail',
        group: 'interiors',
        description: 'A detailed photo showing the USB charging ports.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_armrest_detail: {
        name: 'photo_armrest_detail',
        position: 510,
        position_admin: 660,
        label: 'armrest detail photo',
        group: 'interiors',
        description: 'A detailed photo of the armrest in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_footrest_detail: {
        name: 'photo_footrest_detail',
        position: 520,
        position_admin: 670,
        label: 'footrest detail photo',
        group: 'interiors',
        description: 'A detailed photo of the footrest in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_monitor_detail: {
        name: 'photo_monitor_detail',
        position: 530,
        position_admin: 680,
        label: 'monitor detail photo',
        group: 'interiors',
        description: 'A detailed photo of the monitor in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_outlets_di_current_detail: {
        name: 'photo_outlets_di_current_detail',
        position: 540,
        position_admin: 690,
        label: 'power outlets detail',
        group: 'interiors',
        description: 'A detailed photo of the power outlets in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_front_left: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_front_left',
        position: 700,
        position_admin: 1190,
        label: 'tread condition front tires left',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the front tires left.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_front_right: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_front_right',
        position: 725,
        position_admin: 760,
        label: 'tread condition front tires right',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the front tires right.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_second_axle_right: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_second_axle_right',
        position: 702,
        position_admin: 1200,
        label: 'tread condition second axle tires right',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the second axle tires right.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_second_axle_left: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_second_axle_left',
        position: 700,
        position_admin: 1190,
        label: 'tread condition second axle tires left',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the second axle tires left.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_third_axle_left: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_third_axle_left',
        position: 705,
        position_admin: 1210,
        label: 'tread condition third axle tires left',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the third axle tires left.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_of_tires_with_tread_pattern_and_condition_third_axle_right: {
        name: 'photo_of_tires_with_tread_pattern_and_condition_third_axle_right',
        position: 707,
        position_admin: 1220,
        label: 'tread condition third axle tires right',
        group: 'tire-sizes',
        description: 'A photo showing the tread pattern and condition of the third axle tires right.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_coffee_machine_accessories: {
        name: 'photo_coffee_machine_accessories',
        position: 550,
        position_admin: 710,
        label: 'coffee machine accessories',
        group: 'interiors',
        description: 'A photo of the coffee machine accessories in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_tachograph: {
        name: 'photo_tachograph',
        position: 345,
        position_admin: 380,
        label: 'Tachograph',
        group: 'interiors',
        description: 'A photo of the Tachograph.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_accessories_toilette: {
        name: 'photo_accessories_toilette',
        position: 560,
        position_admin: 520,
        label: 'toilette accessories photo',
        group: 'interiors',
        description: 'A photo showing the toilette accessories inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_accessories_cameras: {
        name: 'photo_accessories_cameras',
        position: 570,
        position_admin: 720,
        label: 'camera accessories photo',
        group: 'interiors',
        description: 'A photo showing the camera accessories inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_accessories_radio: {
        name: 'photo_accessories_radio',
        position: 580,
        position_admin: 390,
        label: 'radio accessories photo',
        group: 'interiors',
        description: 'A photo showing the radio accessories inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_accessories_driver_microphone: {
        name: 'photo_accessories_driver_microphone',
        position: 590,
        position_admin: 330,
        label: 'driver microphone accessories photo',
        group: 'interiors',
        description: "A photo showing the driver's microphone accessories in the vehicle.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_accessories_hostes_microphone: {
        name: 'photo_accessories_hostes_microphone',
        position: 595,
        position_admin: 450,
        label: 'hostes microphone accessories photo',
        group: 'interiors',
        description: "A photo showing the hostes's microphone accessories in the vehicle.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    control_unit_command_signs_indicators: {
        name: 'control_unit_command_signs_indicators',
        position: 660,
        position_admin: 430,
        label: 'command unit indicators',
        group: 'interiors',
        description: 'A photo of the control unit for signs and indicators.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    spare_wheel: {
        name: 'spare_wheel',
        position: 710,
        position_admin: 20,
        label: 'spare wheel',
        group: 'tire-sizes',
        description: 'A photo of the spare wheel in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    jack_e_keys_for_driver: {
        name: 'jack_e_keys_for_driver',
        position: 610,
        position_admin: 730,
        label: 'jack and driver keys',
        group: 'interiors',
        description: 'A photo showing the jack and keys for the driver.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_hatches_floor: {
        name: 'photo_hatches_floor',
        position: 630,
        position_admin: 535,
        label: 'floor hatches photo',
        group: 'interiors',
        description: 'A photo of the floor hatches in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    turntable_photo_internal_if_present_only_18mt: {
        name: 'turntable_photo_internal_if_present_only_18mt',
        position: 640,
        position_admin: 550,
        label: 'turntable internal (18m)',
        group: 'interiors',
        description: 'An internal photo of the turntable if present (only for 18 meters).',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    turntable_photo_external_if_present_only_18mt: {
        name: 'turntable_photo_external_if_present_only_18mt',
        position: 230,
        position_admin: 210,
        label: 'turntable external (18m)',
        group: 'exteriors',
        description: 'An external photo of the turntable if present (only for 18 meters).',
        performTextInpaint: true,
        requiredForToBeScrapped: false,
    },
    photo_external_compartments_various_internal_photo: {
        name: 'photo_external_compartments_various_internal_photo',
        position: 240,
        position_admin: 60,
        label: 'external compartments photo',
        group: 'exteriors',
        description: 'A photo showing various external compartments.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_door_mechanism_internal_photo: {
        name: 'photo_door_mechanism_internal_photo',
        position: 650,
        position_admin: 470,
        label: 'door mechanism photo',
        group: 'interiors',
        description: 'A photo of the internal door mechanism.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_handrails: {
        name: 'photo_handrails',
        position: 660,
        position_admin: 460,
        label: 'handrails photo',
        group: 'interiors',
        description: 'A photo showing the handrails inside the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_brackets_seats: {
        name: 'photo_brackets_seats',
        position: 670,
        position_admin: 710,
        label: 'seat brackets photo',
        group: 'interiors',
        description: 'A photo showing the seat brackets in the vehicle.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_central_seat_with_safety_belt_fastened: {
        name: 'photo_central_seat_with_safety_belt_fastened',
        position: 680,
        position_admin: 540,
        label: 'central seat safety belt',
        group: 'interiors',
        description: 'A photo showing the central seat with the safety belt fastened.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_front_part_from_front: {
        name: 'photo_undercarriage_front_part_from_front',
        position: 730,
        position_admin: 740,
        label: 'front undercarriage from front',
        group: 'undercarriage',
        description: 'A photo showing the front part of the undercarriage from the front.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_front_side_right: {
        name: 'photo_undercarriage_axle_front_side_right',
        position: 740,
        position_admin: 770,
        label: 'front axle right side',
        group: 'undercarriage',
        description: 'A photo of the front axle from the right side of the undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_front_central: {
        name: 'photo_undercarriage_axle_front_central',
        position: 750,
        position_admin: 820,
        label: 'front axle central',
        group: 'undercarriage',
        description: 'A photo of the front axle from the central part of the undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_front_side_left: {
        name: 'photo_undercarriage_axle_front_side_left',
        position: 760,
        position_admin: 830,
        label: 'front axle left side',
        group: 'undercarriage',
        description: 'A photo of the front axle from the left side of the undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_discs_left: {
        name: 'photo_front_discs_left',
        position: 770,
        position_admin: 840,
        label: 'front discs left',
        group: 'undercarriage',
        description: 'A photo of the left front brake discs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_discs_right: {
        name: 'photo_front_discs_right',
        position: 780,
        position_admin: 780,
        label: 'front discs right',
        group: 'undercarriage',
        description: 'A photo of the right front brake discs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_brake_calipers_left: {
        name: 'photo_front_brake_calipers_left',
        position: 790,
        position_admin: 850,
        label: 'front brake calipers left',
        group: 'undercarriage',
        description: 'A photo of the left front brake calipers.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_brake_calipers_right: {
        name: 'photo_front_brake_calipers_right',
        position: 800,
        position_admin: 790,
        label: 'front brake calipers right',
        group: 'undercarriage',
        description: 'A photo of the right front brake calipers.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_shock_absorbers_if_separate_from_springs_right: {
        name: 'photo_front_shock_absorbers_if_separate_from_springs_right',
        position: 810,
        position_admin: 800,
        label: 'front shock absorbers right',
        group: 'undercarriage',
        description: 'A photo of the right front shock absorbers, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_shock_absorbers_if_separate_from_springs_left: {
        name: 'photo_front_shock_absorbers_if_separate_from_springs_left',
        position: 820,
        position_admin: 860,
        label: 'front shock absorbers left',
        group: 'undercarriage',
        description: 'A photo of the left front shock absorbers, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_air_springs_or_group_of_springs_and_shock_absorbers_right: {
        name: 'photo_front_air_springs_or_group_of_springs_and_shock_absorbers_right',
        position: 830,
        position_admin: 810,
        label: 'front air springs right',
        group: 'undercarriage',
        description: 'A photo of the right front air springs or group of springs and shock absorbers.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_front_air_springs_or_group_of_springs_and_shock_absorbers_left: {
        name: 'photo_front_air_springs_or_group_of_springs_and_shock_absorbers_left',
        position: 840,
        position_admin: 870,
        label: 'front air springs left',
        group: 'undercarriage',
        description: 'A photo of the left front air springs or group of springs and shock absorbers.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_central: {
        name: 'photo_undercarriage_central',
        position: 850,
        position_admin: 880,
        label: 'central undercarriage photo',
        group: 'undercarriage',
        description: 'A photo of the central part of the undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_discs_axle_central_left: {
        name: 'photo_discs_axle_central_left',
        position: 860,
        position_admin: 950,
        label: 'axle 2 discs left',
        group: 'undercarriage',
        description: 'A photo of the left brake discs on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_discs_axle_central_right: {
        name: 'photo_discs_axle_central_right',
        position: 870,
        position_admin: 890,
        label: 'axle 2 discs right',
        group: 'undercarriage',
        description: 'A photo of the right brake discs on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_caliper_brakes_axle_central_left: {
        name: 'photo_caliper_brakes_axle_central_left',
        position: 880,
        position_admin: 960,
        label: 'axle 2 caliper brakes left',
        group: 'undercarriage',
        description: 'A photo of the left brake calipers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_caliper_brakes_axle_central_right: {
        name: 'photo_caliper_brakes_axle_central_right',
        position: 890,
        position_admin: 900,
        label: 'axle 2 caliper brakes right',
        group: 'undercarriage',
        description: 'A photo of the right brake calipers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_shock_absorbers_central_axle_if_separate_from_springs_left_1: {
        name: 'photo_shock_absorbers_central_axle_if_separate_from_springs_left_1',
        position: 900,
        position_admin: 970,
        label: 'axle 2 shock absorbers 1 left',
        group: 'undercarriage',
        description: 'A photo of the first left shock absorbers on the axle 2, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_shock_absorbers_central_axle_if_separate_from_springs_left_2: {
        name: 'photo_shock_absorbers_central_axle_if_separate_from_springs_left_2',
        position: 901,
        position_admin: 990,
        label: 'axle 2 shock absorbers 2 left',
        group: 'undercarriage',
        description: 'A photo of the second left shock absorbers on the axle 2, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_shock_absorbers_central_axle_if_separate_from_springs_right_1: {
        name: 'photo_shock_absorbers_central_axle_if_separate_from_springs_right_1',
        position: 910,
        position_admin: 910,
        label: 'axle 2 shock absorbers 1 right',
        group: 'undercarriage',
        description: 'A photo of the first right shock absorbers on the axle 2, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_shock_absorbers_central_axle_if_separate_from_springs_right_2: {
        name: 'photo_shock_absorbers_central_axle_if_separate_from_springs_right_2',
        position: 911,
        position_admin: 920,
        label: 'axle 2 shock absorbers 2 left',
        group: 'undercarriage',
        description: 'A photo of the second right shock absorbers on the axle 2, if they are separate from the springs.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_left_1: {
        name: 'photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_left_1',
        position: 920,
        position_admin: 980,
        label: 'axel 2 air springs 1 left',
        group: 'undercarriage',
        description: 'A photo of the first left air springs or group of springs and shock absorbers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_left_2: {
        name: 'photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_left_2',
        position: 921,
        position_admin: 1000,
        label: 'axel 2 air springs 2 left',
        group: 'undercarriage',
        description: 'A photo of the second left air springs or group of springs and shock absorbers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_right_1: {
        name: 'photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_right_1',
        position: 930,
        position_admin: 930,
        label: 'axel 2 air springs 1 right',
        group: 'undercarriage',
        description: 'A photo of the first right air springs or group of springs and shock absorbers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_right_2: {
        name: 'photo_air_springs_central_axle_or_group_of_springs_and_shock_absorbers_right_2',
        position: 931,
        position_admin: 940,
        label: 'axel 2 air springs 2 right',
        group: 'undercarriage',
        description: 'A photo of the second right air springs or group of springs and shock absorbers on the axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_rear_central: {
        name: 'photo_undercarriage_axle_rear_central',
        position: 940,
        position_admin: 1010,
        label: 'axle 3 central',
        group: 'undercarriage',
        description: 'A photo of the central part of the axle 3 undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_rear_side_left: {
        name: 'photo_undercarriage_axle_rear_side_left',
        position: 950,
        position_admin: 1100,
        label: 'axle 3 left side',
        group: 'undercarriage',
        description: 'A photo of the left side of the axle 3 undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_undercarriage_axle_rear_side_right: {
        name: 'photo_undercarriage_axle_rear_side_right',
        position: 960,
        position_admin: 1020,
        label: 'axle 3 right side',
        group: 'undercarriage',
        description: 'A photo of the right side of the axle 3 undercarriage.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_discs_rear_right: {
        name: 'photo_discs_rear_right',
        position: 970,
        position_admin: 1030,
        label: 'axel 3  discs right',
        group: 'undercarriage',
        description: 'A photo of the right brake discs axle 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_discs_rear_left: {
        name: 'photo_discs_rear_left',
        position: 980,
        position_admin: 1110,
        label: 'axel 3 discs left',
        group: 'undercarriage',
        description: 'A photo of the left brake discs axle 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_caliper_brakes_rear_left: {
        name: 'photo_caliper_brakes_rear_left',
        position: 990,
        position_admin: 1120,
        label: 'axel 3 caliper brakes left',
        group: 'undercarriage',
        description: 'A photo of the left caliper brakes axle 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_caliper_brakes_rear_right: {
        name: 'photo_caliper_brakes_rear_right',
        position: 1000,
        position_admin: 1040,
        label: 'axel 3 caliper brakes right',
        group: 'undercarriage',
        description: 'A photo of the right caliper brakes axle 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_shock_absorbers_right_1: {
        name: 'photo_rear_shock_absorbers_right_1',
        position: 1010,
        position_admin: 1050,
        label: 'axel 3 shock absorbers 1 right',
        group: 'undercarriage',
        description: 'A photo of the right shock absorbers (position 1) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_shock_absorbers_right_2: {
        name: 'photo_rear_shock_absorbers_right_2',
        position: 1020,
        position_admin: 1070,
        label: 'axel 3 shock absorbers 2 right',
        group: 'undercarriage',
        description: 'A photo of the right shock absorbers (position 2) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_shock_absorbers_left_1: {
        name: 'photo_rear_shock_absorbers_left_1',
        position: 1030,
        position_admin: 1130,
        label: 'axel 3 shock absorbers 1 left',
        group: 'undercarriage',
        description: 'A photo of the left shock absorbers (position 1) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_shock_absorbers_left_2: {
        name: 'photo_rear_shock_absorbers_left_2',
        position: 1040,
        position_admin: 1140,
        label: 'axel 3 shock absorbers 2 left',
        group: 'undercarriage',
        description: 'A photo of the left shock absorbers (position 2) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_leaf_spring_if_present_right: {
        name: 'photo_rear_leaf_spring_if_present_right',
        position: 1070,
        position_admin: 1090,
        label: 'axel 2 leaf spring right',
        group: 'undercarriage',
        description: 'A photo of the right leaf spring if present axle 2.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_air_springs_if_present_right_1: {
        name: 'photo_rear_air_springs_if_present_right_1',
        position: 1050,
        position_admin: 1060,
        label: 'axle 3 air springs 1 right',
        group: 'undercarriage',
        description: 'A photo of the right air springs if present (position 1) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_air_springs_if_present_right_2: {
        name: 'photo_rear_air_springs_if_present_right_2',
        position: 1060,
        position_admin: 1080,
        label: 'axle 3 air springs 2 right',
        group: 'undercarriage',
        description: 'A photo of the right air springs if present (position 2) axel 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_air_springs_if_present_left_1: {
        name: 'photo_rear_air_springs_if_present_left_1',
        position: 1080,
        position_admin: 1150,
        label: 'axle 3 air springs 1 left',
        group: 'undercarriage',
        description: 'A photo of the left air springs if present (position 1) axle 3.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_air_springs_if_present_left_2: {
        name: 'photo_rear_air_springs_if_present_left_2',
        position: 1090,
        position_admin: 1160,
        label: 'axle 3 air springs 2 left',
        group: 'undercarriage',
        description: 'A photo of the left rear air springs if present (position 2).',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_rear_leaf_spring_if_present_left: {
        name: 'photo_rear_leaf_spring_if_present_left',
        position: 1100,
        position_admin: 1170,
        label: 'axel 3 leaf spring left',
        group: 'undercarriage',
        description: 'A photo of the left leaf spring if present. Axle 3',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_differential: {
        name: 'photo_differential',
        position: 1110,
        position_admin: 1180,
        label: 'differential photo',
        group: 'undercarriage',
        description: "A photo of the vehicle's differential.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_gearbox: {
        name: 'photo_gearbox',
        position: 1120,
        position_admin: 1230,
        label: 'gearbox photo',
        group: 'undercarriage',
        description: "A photo of the vehicle's gearbox.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_plate_gearbox: {
        name: 'photo_plate_gearbox',
        position: 1240,
        position_admin: 1240,
        label: 'gearbox plate photo',
        group: 'undercarriage',
        description: "A photo of the vehicle's gearbox plate.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_driveshaft: {
        name: 'photo_driveshaft',
        position: 1130,
        position_admin: 1250,
        label: 'driveshaft photo',
        group: 'undercarriage',
        description: "A photo of the vehicle's driveshaft.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_engine_below_without_protection: {
        name: 'photo_engine_below_without_protection',
        position: 1140,
        position_admin: 1260,
        label: 'engine without protection',
        group: 'undercarriage',
        description: 'A photo showing the engine from below without any protection.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_exhaust: {
        name: 'photo_exhaust',
        position: 1150,
        position_admin: 1270,
        label: 'exhaust photo',
        group: 'undercarriage',
        description: "A photo showing the vehicle's exhaust.",
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_axle_central_18_meters: {
        name: 'photo_axle_central_18_meters',
        position: 1160,
        position_admin: 1280,
        label: 'central axle (18m)',
        group: 'undercarriage',
        description: 'A photo of the central axle for 18-meter vehicles.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
    photo_bellows_turntable_18_meters: {
        name: 'photo_bellows_turntable_18_meters',
        position: 690,
        position_admin: 1290,
        label: 'bellows turntable (18m)',
        group: 'undercarriage',
        description: 'A photo showing the bellows of the turntable for 18-meter vehicles.',
        performTextInpaint: false,
        requiredForToBeScrapped: false,
    },
} as const satisfies BusAssetBaseImagesLoose;
